import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { autoridades, type Autoridad } from "@/api/catalogos";
import {
  createOperativo,
  listOperativos,
  transitionFase,
} from "@/api/operativos";
import { useToast } from "@/contexts/ToastContext";
import { formatDate } from "@/lib/utils";

const FASE_LABEL: Record<string, string> = {
  programado: "Programado",
  en_curso: "En curso",
  hit_detectado: "HIT detectado",
  aseguramiento: "Aseguramiento",
  puesta_disposicion: "Puesta a disposición",
  cerrado: "Cerrado",
  cancelado: "Cancelado",
};

const NEXT_FASE: Record<string, string[]> = {
  programado: ["en_curso", "cancelado"],
  en_curso: ["hit_detectado", "cerrado", "cancelado"],
  hit_detectado: ["aseguramiento", "en_curso", "cancelado"],
  aseguramiento: ["puesta_disposicion", "en_curso", "cancelado"],
  puesta_disposicion: ["cerrado", "cancelado"],
  cerrado: [],
  cancelado: [],
};

export function OperativosPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const { data } = useQuery({
    queryKey: ["operativos"],
    queryFn: () => listOperativos({ page: 1, size: 50 }),
  });

  return (
    <div className="p-6 space-y-4" data-testid="page-operativos">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Operativos</h1>
        <button
          className="btn-primary"
          data-testid="operativos-new"
          onClick={() => setCreating(true)}
        >
          Nuevo
        </button>
      </header>

      <div className="card">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted-200">
            <tr className="border-b border-border">
              <th className="py-2">Nombre</th>
              <th>Fase</th>
              <th>Inicio</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {(data?.items ?? []).map((op) => (
              <tr key={op.id} className="border-b border-border/40" data-testid={`operativo-row-${op.id}`}>
                <td className="py-2">{op.nombre}</td>
                <td>{FASE_LABEL[op.fase] ?? op.fase}</td>
                <td>{formatDate(op.programadoInicio ?? op.createdAt)}</td>
                <td className="space-x-1">
                  {(NEXT_FASE[op.fase] ?? []).map((next) => (
                    <button
                      key={next}
                      data-testid={`operativo-${op.id}-to-${next}`}
                      className="btn-ghost text-xs"
                      onClick={async () => {
                        try {
                          await transitionFase(op.id, { faseNueva: next });
                          await qc.invalidateQueries({ queryKey: ["operativos"] });
                          toast.push(`Transición → ${next}`, "success");
                        } catch {
                          toast.push("Transición rechazada", "error");
                        }
                      }}
                    >
                      → {next}
                    </button>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating && (
        <CreateDialog
          onCancel={() => setCreating(false)}
          onCreated={async () => {
            setCreating(false);
            await qc.invalidateQueries({ queryKey: ["operativos"] });
          }}
        />
      )}
    </div>
  );
}

function CreateDialog({ onCancel, onCreated }: { onCancel: () => void; onCreated: () => Promise<void> }) {
  const toast = useToast();
  const [nombre, setNombre] = useState("");
  const [autoridadId, setAutoridadId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const autoridadesQ = useQuery<Autoridad[]>({
    queryKey: ["autoridades"],
    queryFn: autoridades.list,
  });

  async function submit() {
    if (!nombre || !autoridadId) return;
    setSubmitting(true);
    try {
      await createOperativo({ nombre, autoridadId });
      toast.push("Operativo creado", "success");
      await onCreated();
    } catch {
      toast.push("Error al crear", "error");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 grid place-items-center bg-black/60 p-4" data-testid="operativo-create">
      <div className="card w-full max-w-md space-y-3">
        <h2 className="font-semibold">Nuevo operativo</h2>
        <input
          className="input"
          placeholder="Nombre"
          value={nombre}
          data-testid="operativo-nombre"
          onChange={(e) => setNombre(e.target.value)}
        />
        <select
          className="input"
          data-testid="operativo-autoridad"
          value={autoridadId ?? ""}
          onChange={(e) => setAutoridadId(Number(e.target.value) || null)}
        >
          <option value="">Selecciona autoridad…</option>
          {(autoridadesQ.data ?? []).map((a) => (
            <option key={a.id} value={a.id}>
              {a.corporacion} — {a.responsable}
            </option>
          ))}
        </select>
        <div className="flex gap-2">
          <button onClick={onCancel} className="btn-ghost flex-1">
            Cancelar
          </button>
          <button
            onClick={submit}
            disabled={!nombre || !autoridadId || submitting}
            data-testid="operativo-submit"
            className="btn-primary flex-1"
          >
            Crear
          </button>
        </div>
      </div>
    </div>
  );
}
